/**
 * LL Kiwi size guide.
 *
 * Kiwi Size Chart decides in the browser whether a product has a chart: its
 * script matches the data attributes on `#KiwiSizingChart` (the custom-liquid
 * block in the PDP "Size guide" accordion row) against the rules in the Kiwi
 * dashboard and fills the div, or leaves it empty. Liquid renders the same empty
 * div either way, so the theme cannot know at render time.
 *
 * `assets/ll-app-overrides.css` hides the size-guide row and every link to it
 * while the div is unfilled. This file only flips the switch: it marks the div
 * `data-ll-kiwi-ready` once Kiwi has put a chart in it, which reveals both. If
 * Kiwi finds no chart, or its script never loads, the attribute is never set and
 * the row and links stay hidden — an empty row would be no use to anyone.
 *
 * Observed for the life of the page rather than once, so a chart that arrives
 * late, or is cleared again by Kiwi, still shows or hides the row correctly.
 *
 * Loaded on product templates only, from snippets/hw-scripts.liquid. Wrapped in an
 * IIFE because it is a classic script: its top-level names would share the page's
 * global scope, and another script already declares `observer` there.
 */

(() => {
  const READY_ATTR = 'data-ll-kiwi-ready';

  /** Kiwi's chart is a table, an image, or both; whitespace alone is not a chart. */
  function hasChart(chart) {
    return Boolean(chart.querySelector('table, img, iframe') || chart.textContent.trim());
  }

  function sync(chart) {
    chart.toggleAttribute(READY_ATTR, hasChart(chart));
  }

  let observer = null;

  function init() {
    observer?.disconnect();
    observer = null;

    const chart = document.getElementById('KiwiSizingChart');
    if (!chart) return;

    sync(chart);
    observer = new MutationObserver(() => sync(chart));
    observer.observe(chart, { childList: true, subtree: true, characterData: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // A colour-sibling swap (assets/hw-product-siblings.js) and the theme editor both replace
  // the whole section, bringing a fresh, empty #KiwiSizingChart for Kiwi to fill again.
  document.addEventListener('shopify:section:load', init);
})();
